import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';
import {
  BimEntityProperties,
  BimMeasureMode,
  BimMeasurement,
  BimSectionPlane,
  BimSelection,
  BimTreeNode,
  BimViewerHandle,
} from 'Data/interfaces/BimViewer/IBimViewerPort';

// Seleção corrente do viewer. `ids` é o conjunto sobre o qual isolar/ocultar
// agem: um elemento (clique no 3D ou numa folha da árvore) ou tudo o que está
// sob um nó da árvore (pavimento, grupo de tipo).
export interface IBimSelection {
  ids: number[];
  /** Elemento cujas propriedades o painel mostra; null em nó de contêiner. */
  primaryId: number | null;
  /** Nó da árvore correspondente, quando a seleção veio de lá. */
  treeKey?: string;
}

// Teto da agregação de propriedades. Um nó de árvore pode cobrir milhares de
// elementos; agregar tudo travaria a UI por um painel que ninguém leria inteiro.
const MAX_AGGREGATED_PROPERTIES = 200;

const DEFAULT_SECTION: BimSectionPlane = {
  axis: 'down',
  position: 50,
  enabled: false,
  flipped: false,
};

/** Todos os elementos abaixo de um nó (folhas `type-group` carregam os ids). */
const collectIds = (node: BimTreeNode, out: number[] = []): number[] => {
  if (node.expressIds) { out.push(...node.expressIds); }
  for (const child of node.children) { collectIds(child, out); }
  return out;
};

/**
 * Quantos elementos de cada nó estão ocultos, por chave. Uma passada só na
 * árvore inteira a cada mudança de visibilidade — a alternativa (cada linha da
 * árvore contar os seus ids ao renderizar) refaz o mesmo trabalho a cada frame,
 * e um pavimento sozinho tem dezenas de milhares de ids.
 */
export const countHidden = (nodes: BimTreeNode[], hidden: Set<number>): Map<string, number> => {
  const counts = new Map<string, number>();
  const walk = (node: BimTreeNode): number => {
    let count = 0;
    if (node.expressIds) {
      for (const id of node.expressIds) { if (hidden.has(id)) { count++; } }
    }
    for (const child of node.children) { count += walk(child); }
    counts.set(node.key, count);
    return count;
  };
  nodes.forEach(walk);
  return counts;
};

/** expressId → chave do nó `type-group` que o contém (para o caminho 3D → árvore). */
const indexElements = (nodes: BimTreeNode[]): Map<number, string> => {
  const index = new Map<number, string>();
  const walk = (node: BimTreeNode) => {
    if (node.expressIds) {
      for (const id of node.expressIds) { index.set(id, node.key); }
    }
    node.children.forEach(walk);
  };
  nodes.forEach(walk);
  return index;
};

/** Caminho de chaves da raiz até o nó alvo — o que precisa ser expandido. */
const pathTo = (nodes: BimTreeNode[], targetKey: string): string[] => {
  const walk = (node: BimTreeNode, trail: string[]): string[] | null => {
    if (node.key === targetKey) { return trail; }
    for (const child of node.children) {
      const hit = walk(child, [...trail, node.key]);
      if (hit) { return hit; }
    }
    return null;
  };
  for (const root of nodes) {
    const hit = walk(root, []);
    if (hit) { return hit; }
  }
  return [];
};

interface IUseBim3DToolsParams {
  handleRef: MutableRefObject<BimViewerHandle | null>;
  /**
   * Modelo a que estas ferramentas se referem, na cena federada (uma instância
   * por modelo, uma árvore por modelo). Ausente = viewer de arquivo único, onde
   * a cena tem um modelo só e o índice não faz sentido.
   */
  modelIndex?: number;
}

/**
 * Estado das ferramentas do viewer 3D: árvore espacial, seleção (nos dois
 * sentidos), propriedades do elemento e plano de corte.
 *
 * O motor é a fonte da verdade da cena — aqui só guardamos o que a UI precisa
 * desenhar. Nada de duplicar geometria ou visibilidade no React.
 */
export const useBim3DTools = ({ handleRef, modelIndex }: IUseBim3DToolsParams) => {
  const [tree, setTree] = useState<BimTreeNode[]>([]);
  // O data model chega DEPOIS do render (o server o grava em background), então a
  // árvore tem um estado de espera próprio — sem ele o painel só ficaria vazio,
  // sem o usuário saber se está carregando ou se o modelo não tem hierarquia.
  const [dataModelState, setDataModelState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [selection, setSelection] = useState<IBimSelection | null>(null);
  // Lista, não um só: com vários elementos selecionados o painel agrega (soma as
  // quantidades, mostra "*Varia*" no que difere) — mesmo comportamento do 2D.
  const [properties, setProperties] = useState<BimEntityProperties[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [section, setSectionState] = useState<BimSectionPlane>(DEFAULT_SECTION);
  const [isolated, setIsolated] = useState(false);
  const [multiSelect, setMultiSelect] = useState(false);
  // Ghost do modelo pedido na toolbar (fica até desligar), o ghost aplicado a
  // uma seleção e o X-Ray temporário do foco / duplo clique (sai com a seleção).
  // A toolbar mostra os três como um botão só.
  const [ghostPinned, setGhostPinned] = useState(false);
  const [ghostedSelection, setGhostedSelection] = useState(false);
  const [focusGhost, setFocusGhost] = useState(false);
  // Espelho do que está oculto na cena. O motor continua sendo a fonte da
  // verdade do render; isto existe só para a árvore saber que ícone desenhar.
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(() => new Set());
  const [measureMode, setMeasureModeState] = useState<BimMeasureMode>('none');
  const [measurements, setMeasurements] = useState<BimMeasurement[]>([]);

  const elementIndex = useRef<Map<number, string>>(new Map());
  // Os ids de um nó não mudam enquanto a árvore for a mesma; recalcular a cada
  // clique no olho atravessaria a subárvore inteira de novo.
  const nodeIds = useRef<Map<string, number[]>>(new Map());

  // Chega quando o data model termina de baixar/decodificar — depois do render.
  const handleDataModelReady = useCallback((detail: { available: boolean }) => {
    if (!detail.available) { setDataModelState('unavailable'); return; }
    const nodes = handleRef.current?.getSpatialTree(modelIndex) ?? [];
    elementIndex.current = indexElements(nodes);
    nodeIds.current = new Map();
    setTree(nodes);
    setDataModelState(nodes.length > 0 ? 'ready' : 'unavailable');
    // Raízes abertas: uma árvore toda fechada não comunica que há conteúdo.
    setExpandedKeys(nodes.map((node) => node.key));
  }, [handleRef, modelIndex]);

  const reset = useCallback(() => {
    setTree([]);
    setDataModelState('loading');
    setSelection(null);
    setProperties([]);
    setExpandedKeys([]);
    setIsolated(false);
    setMultiSelect(false);
    setGhostPinned(false);
    setGhostedSelection(false);
    setFocusGhost(false);
    setHiddenIds(new Set());
    setMeasureModeState('none');
    setMeasurements([]);
    setSectionState(DEFAULT_SECTION);
    elementIndex.current = new Map();
    nodeIds.current = new Map();
  }, []);

  /**
   * Carrega as propriedades de TODA a seleção — é o que permite o painel somar
   * quantidades e marcar o que varia, como no viewer 2D.
   *
   * O teto existe porque um nó de árvore pode cobrir milhares de elementos, e
   * agregar tudo isso trava a UI por um painel que ninguém leria linha a linha.
   */
  const loadProperties = useCallback((expressIds: number[]) => {
    const handle = handleRef.current;
    if (!handle || expressIds.length === 0) { setProperties([]); return; }

    const list: BimEntityProperties[] = [];
    for (const expressId of expressIds.slice(0, MAX_AGGREGATED_PROPERTIES)) {
      const entity = handle.getEntityProperties(expressId, modelIndex);
      if (entity) { list.push(entity); }
    }
    setProperties(list);
  }, [handleRef, modelIndex]);

  // 3D → árvore: revela o nó do elemento clicado e mostra suas propriedades.
  const handleSelectionChanged = useCallback((next: BimSelection) => {
    if (next.expressId === null) {
      // Multi-seleção pode ter deselecionado o último clicado e ainda ter itens.
      const remaining = next.expressIds ?? [];
      setSelection(remaining.length > 0 ? { ids: remaining, primaryId: null } : null);
      setProperties([]);
      return;
    }

    const treeKey = elementIndex.current.get(next.expressId);
    if (treeKey) {
      setExpandedKeys((keys) => {
        const trail = [...pathTo(tree, treeKey), treeKey];
        return Array.from(new Set(keys.concat(trail)));
      });
    }

    // `expressIds` é a seleção inteira (multi); `expressId` é o último clicado,
    // e é dele que o painel mostra as propriedades.
    const ids = next.expressIds?.length ? next.expressIds : [next.expressId];
    setSelection({ ids, primaryId: next.expressId, treeKey });
    loadProperties(ids);
  }, [tree, loadProperties]);

  // Árvore → 3D: folha destaca e enquadra o elemento; contêiner enquadra o
  // conjunto (e alimenta isolar/ocultar com todos os seus elementos).
  const selectTreeNode = useCallback((node: BimTreeNode) => {
    const handle = handleRef.current;
    if (!handle) { return; }

    const isElement = node.kind === 'element';
    const ids = isElement && node.expressId !== undefined
      ? [node.expressId]
      : collectIds(node);

    if (ids.length === 0) { return; }

    if (isElement && node.expressId !== undefined) {
      handle.select(node.expressId, { frame: true, modelIndex });
      setSelection({ ids, primaryId: node.expressId, treeKey: node.key });
      loadProperties(ids);
      return;
    }

    // Contêiner: sem elemento único a destacar, limpa o highlight e enquadra.
    handle.select(null);
    handle.frameEntities(ids, modelIndex);
    setSelection({ ids, primaryId: null, treeKey: node.key });
    // Contêiner: sem elemento único, o painel agrega o conjunto (soma das
    // quantidades do pavimento / do grupo de tipo).
    loadProperties(ids);
  }, [handleRef, loadProperties, modelIndex]);

  const isolateSelection = useCallback(() => {
    const ids = selection?.ids;
    if (!ids || ids.length === 0) { return; }
    handleRef.current?.isolate(ids);
    setIsolated(true);
  }, [handleRef, selection]);

  const hideSelection = useCallback(() => {
    const ids = selection?.ids;
    if (!ids || ids.length === 0) { return; }
    handleRef.current?.hide(ids);
    setHiddenIds((current) => {
      const next = new Set(current);
      for (const id of ids) { next.add(id); }
      return next;
    });
    // O que sumiu não pode continuar selecionado (nem mantendo o X-Ray ligado
    // contra um alvo invisível).
    handleRef.current?.clearSelection();
    setSelection(null);
    setProperties([]);
    setFocusGhost(false);
  }, [handleRef, selection]);

  /**
   * Botão único de fantasma. Com seleção, ela fica translúcida (e continua
   * selecionada: o painel de propriedades segue aberto); sem seleção, o modelo
   * inteiro. Clicar de novo desfaz os dois.
   */
  const toggleGhost = useCallback(() => {
    const ids = selection?.ids;

    if (ghostPinned || ghostedSelection || focusGhost) {
      handleRef.current?.clearGhost();
      setGhostPinned(false);
      setGhostedSelection(false);
      setFocusGhost(false);
      return;
    }

    if (ids && ids.length > 0) {
      handleRef.current?.ghost(ids);
      setGhostedSelection(true);
      return;
    }

    handleRef.current?.setGhostMode(true);
    setGhostPinned(true);
  }, [handleRef, selection, ghostPinned, ghostedSelection, focusGhost]);

  /**
   * O olho da árvore. Um nó só volta inteiro quando está inteiro oculto — no
   * estado parcial (alguém escondeu um filho lá dentro) o clique oculta o resto,
   * que é o que o usuário espera de um controle no pai.
   */
  const toggleNodeVisibility = useCallback((node: BimTreeNode) => {
    const handle = handleRef.current;
    if (!handle) { return; }

    let ids = nodeIds.current.get(node.key);
    if (!ids) {
      ids = node.kind === 'element' && node.expressId !== undefined
        ? [node.expressId]
        : collectIds(node);
      nodeIds.current.set(node.key, ids);
    }
    if (ids.length === 0) { return; }

    // Chamar o motor FORA do updater: em StrictMode o updater roda duas vezes, e
    // hide/show sairiam em dobro.
    const allHidden = ids.every((id) => hiddenIds.has(id));
    const next = new Set(hiddenIds);
    if (allHidden) {
      handle.show(ids);
      for (const id of ids) { next.delete(id); }
    } else {
      handle.hide(ids);
      for (const id of ids) { next.add(id); }
    }
    setHiddenIds(next);

    // Esconder o que está selecionado deixaria o painel de propriedades e o
    // X-Ray apontando pra um alvo que não está mais na tela.
    if (!allHidden && selection?.ids.some((id) => ids!.includes(id))) {
      handle.clearSelection();
      setSelection(null);
      setProperties([]);
      setFocusGhost(false);
    }
  }, [handleRef, selection, hiddenIds]);

  /**
   * Volta ao padrão: tudo à vista, nada selecionado, sem fantasma nem
   * isolamento. É o que o Esc faz na cena — aqui só espelhamos o resultado,
   * venha do teclado (`handleViewReset`) ou de quem chamar isto.
   */
  const handleViewReset = useCallback(() => {
    setIsolated(false);
    setHiddenIds(new Set());
    setGhostPinned(false);
    setGhostedSelection(false);
    setFocusGhost(false);
    setSelection(null);
    setProperties([]);
  }, []);

  const resetView = useCallback(() => {
    handleRef.current?.resetView();
    handleViewReset();
  }, [handleRef, handleViewReset]);

  /**
   * Alterna o modo de medição — clicar no modo ativo desliga. Enquanto mede, o
   * clique no 3D é ponto de medida, então a seleção corrente sai da frente
   * (senão o X-Ray e o painel ficam apontando pra um elemento que o usuário não
   * está mais mexendo).
   */
  const toggleMeasureMode = useCallback((mode: Exclude<BimMeasureMode, 'none'>) => {
    const next = measureMode === mode ? 'none' : mode;
    handleRef.current?.setMeasureMode(next);
    setMeasureModeState(next);
    if (next !== 'none') {
      handleRef.current?.clearSelection();
      setSelection(null);
      setProperties([]);
      setFocusGhost(false);
    }
  }, [handleRef, measureMode]);

  /**
   * O motor é dono das medições (é ele quem desenha o overlay) e do modo — o Esc
   * na cena sai da ferramenta lá dentro. Aqui só espelhamos os dois.
   */
  const handleMeasureChanged = useCallback(
    (state: { mode: BimMeasureMode; measurements: BimMeasurement[] }) => {
      setMeasureModeState(state.mode);
      setMeasurements(state.measurements);
    },
    [],
  );

  const clearMeasurements = useCallback(() => {
    handleRef.current?.clearMeasurements();
  }, [handleRef]);

  const frameSelection = useCallback(() => {
    const ids = selection?.ids;
    if (!ids?.length) { return; }
    // Enquadrar sozinho costuma deixar o elemento atrás de parede/laje; o foco
    // enquadra E deixa o resto translúcido, que é o que mostra a peça de fato.
    if (selection?.primaryId !== null) { handleRef.current?.focusSelection(); }
    else { handleRef.current?.frameEntities(ids); }
    setFocusGhost(true);
  }, [handleRef, selection]);

  const toggleMultiSelect = useCallback(() => {
    setMultiSelect((current) => {
      handleRef.current?.setMultiSelect(!current);
      return !current;
    });
  }, [handleRef]);

  const clearSelection = useCallback(() => {
    handleRef.current?.clearSelection();
    setSelection(null);
    setProperties([]);
    setFocusGhost(false);
  }, [handleRef]);

  // Corte: o motor aplica no frame seguinte, então arrastar o slider já corta
  // em tempo real — sem recarregar nem re-tesselar nada.
  const setSection = useCallback((next: Partial<BimSectionPlane>) => {
    setSectionState((current) => {
      const merged = { ...current, ...next };
      handleRef.current?.setSectionPlane(merged.enabled ? merged : null);
      return merged;
    });
  }, [handleRef]);

  const hasSelection = !!selection?.ids.length;

  // Recalcula só quando a visibilidade (ou a árvore) muda — não a cada render.
  const hiddenCountByKey = useMemo(
    () => (hiddenIds.size > 0 ? countHidden(tree, hiddenIds) : new Map<string, number>()),
    [tree, hiddenIds],
  );

  return useMemo(() => ({
    tree,
    dataModelState,
    dataModelReady: dataModelState === 'ready',
    expandedKeys,
    setExpandedKeys,
    selection,
    hasSelection,
    selectionCount: selection?.ids.length ?? 0,
    properties,
    section,
    isolated,
    multiSelect,
    ghost: ghostPinned || ghostedSelection || focusGhost,
    hiddenIds,
    hiddenCountByKey,
    hasHidden: hiddenIds.size > 0,
    measureMode,
    measurements,
    toggleMeasureMode,
    handleMeasureChanged,
    clearMeasurements,
    handleDataModelReady,
    handleSelectionChanged,
    selectTreeNode,
    isolateSelection,
    hideSelection,
    toggleNodeVisibility,
    resetView,
    handleViewReset,
    frameSelection,
    toggleMultiSelect,
    toggleGhost,
    clearSelection,
    setSection,
    reset,
  }), [
    tree, dataModelState, expandedKeys, selection, hasSelection, properties, section, isolated,
    multiSelect, ghostPinned, ghostedSelection, focusGhost, hiddenIds, hiddenCountByKey, measureMode, measurements,
    toggleMeasureMode, handleMeasureChanged, clearMeasurements, handleDataModelReady,
    handleSelectionChanged, selectTreeNode, isolateSelection, hideSelection, handleViewReset,
    toggleNodeVisibility, resetView, frameSelection, toggleMultiSelect, toggleGhost, clearSelection,
    setSection, reset,
  ]);
};

export type Bim3DTools = ReturnType<typeof useBim3DTools>;
